import React, { useEffect, useState } from "react";
import { DataTable } from "@app/components/atoms";
import { IconButton, CircularProgress } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import RestoreIcon from "@mui/icons-material/RestoreFromTrash";
import { useTranslation } from "react-i18next";
import PermissionsDialog from "./PermissionsDialog";
import { apiGetUserAdminPermissions, apiRestoreUser, getUser } from "@app/api";
import { AdminPermissions } from "@app/constants";
import { ConfirmDialog } from "@app/pages/Features/GroupList/GroupDetail/Components/InspectionForm/ConfirmDialog";

export type UserSearchResult = {
  userId?: string;
  email?: string;
  givenName?: string;
  surname?: string;
  searchId?: string;
  [key: string]: any;
};

type Props = {
  rows?: UserSearchResult[];
  loading?: boolean;
  onRowClick?: (row: UserSearchResult) => void;
  isManageMode?: boolean;
  onUserRestored?: () => void;
};

const SearchUserTable: React.FC<Props> = ({
  rows,
  loading,
  onRowClick,
  isManageMode,
  onUserRestored,
}) => {
  const { t } = useTranslation();

  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(
    null,
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [permissionsLoading, setPermissionsLoading] = useState<boolean>(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [, setCurrentUserLoading] = useState<boolean>(false);
  const [initialPermissions, setInitialPermissions] = useState<
    Partial<Record<AdminPermissions, boolean>>
  >({});

  const [restoreTarget, setRestoreTarget] = useState<UserSearchResult | null>(
    null,
  );
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      setCurrentUserLoading(true);
      try {
        const resp = await getUser();
        const userId = resp.data?.data?.userId;
        if (userId) {
          setCurrentUserId(userId);
        }
      } catch (err) {
        console.error("Failed to fetch current user:", err);
      } finally {
        setCurrentUserLoading(false);
      }
    };

    fetchCurrentUser();
  }, []);

  const handleOpenDialog = async (row: UserSearchResult) => {
    setSelectedUser(row);
    setDialogOpen(true);

    if (!row?.userId) {
      setInitialPermissions({});
      return;
    }

    setPermissionsLoading(true);

    try {
      const resp = await apiGetUserAdminPermissions(row.userId);
      const perms: Partial<Record<AdminPermissions, boolean>> =
        resp.data?.data || {};
      setInitialPermissions(perms);
    } catch (err) {
      console.error("Failed to fetch permissions:", err);
      setInitialPermissions({});
    } finally {
      setPermissionsLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
    setInitialPermissions({});
    setPermissionsLoading(false);
  };

  const handleOpenRestoreDialog = (row: UserSearchResult) => {
    setRestoreTarget(row);
    setRestoreDialogOpen(true);
  };

  const handleCloseRestoreDialog = () => {
    setRestoreDialogOpen(false);
    setRestoreTarget(null);
  };

  const handleConfirmRestore = async () => {
    if (!restoreTarget?.userId) return;

    setRestoring(true);
    try {
      await apiRestoreUser(restoreTarget.userId);
      onUserRestored?.();
    } catch (err) {
      console.error("Failed to restore user:", err);
    } finally {
      setRestoring(false);
      handleCloseRestoreDialog();
    }
  };

  const columns: any[] = [
    {
      key: "userId",
      title: t("userId"),
      dataIndex: "userId",
      style: { minWidth: 160 },
      className: "txt-pre-wrap",
    },
    {
      key: "email",
      title: t("searchUserEmail"),
      dataIndex: "email",
      style: { minWidth: 220 },
      className: "txt-pre-wrap",
    },
    {
      key: "givenName",
      title: t("givenName"),
      dataIndex: "givenName",
      style: { minWidth: 120 },
      className: "txt-pre-wrap",
    },
    {
      key: "surname",
      title: t("surname"),
      dataIndex: "surname",
      style: { minWidth: 120 },
      className: "txt-pre-wrap",
    },
    {
      key: "searchId",
      title: t("searchId"),
      dataIndex: "searchId",
      style: { minWidth: 120 },
      className: "txt-pre-wrap",
    },
    {
      key: "currentStatus",
      title: t("currentStatus"),
      dataIndex: "currentStatus",
      style: { minWidth: 120 },
      className: "txt-pre-wrap",
      render: (value: string) => {
        if (value === "DELETED") return t("Deleted");
        if (value === "PENDING_DELETION") return t("PendingDeletion");
        return t("Active");
      },
    },
  ];

  if (isManageMode) {
    columns.push({
      key: "edit",
      title: t("editPermissions"),
      dataIndex: "edit",
      align: "center",
      style: { minWidth: 120 },
      forceRender: true,
      render: (_: any, row: UserSearchResult) => {
        if (row.currentStatus === "DELETED") {
          return null;
        }

        if (row.currentStatus === "PENDING_DELETION") {
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                justifyContent: "center",
              }}
            >
              <IconButton
                aria-label={t("restoreUser") as string}
                onClick={() => handleOpenRestoreDialog(row)}
                size="small"
              >
                <RestoreIcon fontSize="small" />
              </IconButton>

              {restoreTarget?.userId === row.userId && restoring && (
                <CircularProgress size={16} />
              )}
            </div>
          );
        }

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              justifyContent: "center",
            }}
          >
            <IconButton
              aria-label={t("editPermissions") as string}
              onClick={() => handleOpenDialog(row)}
              size="small"
            >
              <EditIcon fontSize="small" />
            </IconButton>

            {selectedUser?.userId === row.userId && permissionsLoading && (
              <CircularProgress size={16} />
            )}
          </div>
        );
      },
    });
  }

  return (
    <div>
      <DataTable
        rowKey="userId"
        columns={columns}
        dataSource={rows || []}
        textEmptyData="noUsers"
        onRequestSort={() => {}}
        loading={loading}
      />
      <PermissionsDialog
        open={dialogOpen}
        user={selectedUser || undefined}
        currentUserId={currentUserId ?? ""}
        onClose={handleCloseDialog}
        initialPermissions={initialPermissions}
        loading={permissionsLoading}
        onSave={(permissions) => setInitialPermissions(permissions)}
      />
      <ConfirmDialog
        open={restoreDialogOpen}
        setOpen={setRestoreDialogOpen}
        title="restoreUserConfirm.title"
        content="restoreUserConfirm.description"
        btnCancelText="dialogReport.cancel"
        btnSubmitText="restoreUser"
        classBtnSubmit="btn-primary"
        onCancel={handleCloseRestoreDialog}
        onSubmit={handleConfirmRestore}
      />
    </div>
  );
};

export default SearchUserTable;
